import React, { useMemo, useState } from 'react';
import { Filter, X } from 'lucide-react';
import { useI18n } from '../context/I18nContext';

/**
 * Small chip-based filter panel above the quote list.
 *
 * Props:
 *   quotes            - full quote array
 *   insurerProfiles   - dict keyed by insurer name -> profile
 *   product           - 'home' | 'health'
 *   onFiltered(list)  - called whenever the filtered list changes
 */
export default function QuoteFilters({ quotes, insurerProfiles = {}, product, onFiltered }) {
  const { t } = useI18n();
  const [active, setActive] = useState({});   // { chipKey: true }

  // Chips available. Health gets "Includes dental" — home gets "Water damage cover".
  const chips = useMemo(() => {
    const base = [
      {
        key: 'english_plus',
        label: t('filters.english_plus', 'English support: Excellent+'),
        test: (q, p) => p && ['excellent', 'good'].includes(p.english_support),
      },
      {
        key: 'english_excellent_only',
        label: t('filters.english_excellent', 'English support: Excellent only'),
        test: (q, p) => p?.english_support === 'excellent',
      },
      {
        key: 'am_best_a_plus',
        label: t('filters.am_best_a_plus', 'AM Best A+ only'),
        test: (q, p) => (p?.am_best_rating || '').toUpperCase().replace(/\s/g, '').startsWith('A+'),
      },
      {
        key: 'am_best_a',
        label: t('filters.am_best_a', 'AM Best A or better'),
        test: (q, p) => (p?.am_best_rating || '').toUpperCase().startsWith('A'),
      },
    ];
    if (product === 'health') {
      base.push({
        key: 'dental',
        label: t('filters.dental', 'Includes dental'),
        test: (q) => (q.highlights || []).some((h) => /dental/i.test(h)) && !/basic dental|no dental/i.test((q.highlights || []).join(' ')),
      });
      base.push({
        key: 'no_copay',
        label: t('filters.no_copay', 'No copay'),
        test: (q) => (q.highlights || []).some((h) => /^no copay/i.test(h)),
      });
      base.push({
        key: 'international',
        label: t('filters.international', 'Worldwide cover'),
        test: (q) => (q.highlights || []).some((h) => /worldwide|international/i.test(h)),
      });
    }
    if (product === 'home') {
      base.push({
        key: 'water_damage',
        label: t('filters.water_damage', 'Water damage'),
        test: (q) => (q.highlights || []).some((h) => /water damage/i.test(h)),
      });
      base.push({
        key: 'legal',
        label: t('filters.legal_defence', 'Legal defence'),
        test: (q) => (q.highlights || []).some((h) => /legal/i.test(h)),
      });
    }
    return base;
  }, [product, t]);

  const activeKeys = Object.keys(active).filter((k) => active[k]);
  const filtered = useMemo(() => {
    if (activeKeys.length === 0) return quotes;
    return quotes.filter((q) => {
      const profile = insurerProfiles[q.insurer] || null;
      // A quote passes if it satisfies EVERY active chip (AND semantics).
      return activeKeys.every((k) => {
        const chip = chips.find((c) => c.key === k);
        if (!chip) return true;
        try { return chip.test(q, profile); } catch { return false; }
      });
    });
  }, [quotes, insurerProfiles, activeKeys, chips]);

  // Emit filtered list up to the wizard
  React.useEffect(() => {
    if (typeof onFiltered === 'function') onFiltered(filtered);
  }, [filtered, onFiltered]);

  const toggle = (key) => setActive((a) => ({ ...a, [key]: !a[key] }));
  const clear = () => setActive({});

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold mr-2">
          <Filter size={12} /> {t('filters.title', 'Filter')}
        </span>
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => toggle(c.key)}
            data-testid={`filter-${c.key}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              active[c.key]
                ? 'bg-[#0d3a8a] text-white border-[#0d3a8a]'
                : 'bg-white text-slate-600 border-slate-200 hover:border-[#0d3a8a]/40'
            }`}
          >
            {c.label}
          </button>
        ))}
        {activeKeys.length > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 ml-1"
            data-testid="filter-clear"
          >
            <X size={12} /> {t('filters.clear', 'Clear filters')}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-2" data-testid="filter-count">
        {t('filters.showing', 'Showing')} <b className="text-[#0b1f3a]">{filtered.length}</b> {t('filters.of', 'of')} <b>{quotes.length}</b> {t('filters.insurers', 'insurers')}
        {filtered.length === 0 && (
          <span className="ml-2 text-rose-600 font-semibold">— {t('filters.none_match', 'No insurer matches all selected filters.')}</span>
        )}
      </p>
    </div>
  );
}
